import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { spkStore } from './spkStore';
import { CABANG_LABELS } from './dummyData';

// ── Palette aligned with the Production Dashboard
const C = {
  indigo: '#4f46e5',
  ink: '#111827',
  slate: '#475569',
  sub: '#64748b',
  muted: '#94a3b8',
  line: '#e2e8f0',
  panel: '#f8fafc',
  red: '#dc2626',
  redBg: '#fef2f2',
  green: '#16a34a',
  white: '#fff',
};

const QTY_MIN = 1;
const QTY_MAX = 1000000;
const ALLOC_MIN = 0;
const ALLOC_MAX = 999999999;
const PIC_MAX = 100;
const CATATAN_MAX = 500;

const PIC_SUGGESTIONS = ['Admin', 'Pak Hendra', 'Bu Sari'];

const isIntegerString = (s) => /^\d+$/.test(s);

const labelStyle = { fontSize: 11, fontWeight: 700, color: C.slate, textTransform: 'uppercase', letterSpacing: '.3px' };
const fieldStyle = {
  width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 7,
  border: `1px solid ${C.line}`, color: C.ink, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit',
};
const errTextStyle = { fontSize: 11, color: C.red, fontWeight: 600 };

const SpkCreateDialog = ({ item, branches, onClose, onCreated }) => {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const firstFieldRef = useRef(null);
  // The element focused before the dialog opened (the "Buat SPK" trigger).
  const triggerRef = useRef(typeof document !== 'undefined' ? document.activeElement : null);

  const defaultQty = useMemo(() => Math.abs(item?.nasional?.stok_min_sp ?? 0), [item]);

  // ── Production quantity
  const [qtyText, setQtyText] = useState(String(defaultQty));

  // ── Branch deficit allocation rows
  const [branchRows, setBranchRows] = useState(() =>
    (branches || [])
      .map((b) => b.key)
      .filter((c) => item?.cabang?.[c])
      .map((c) => {
        const cb = item.cabang[c];
        const alloc = cb.stok_min_sp < 0 ? Math.abs(cb.stok_min_sp) : 0;
        return {
          cabang: c,
          stok: cb.stok_akhir,
          sp: cb.sp_belum,
          selisih: cb.stok_min_sp,
          value: alloc,            // last valid committed allocation
          text: String(alloc),     // current input text
        };
      })
  );

  // ── PIC
  const [pic, setPic] = useState(item?.pic && item.pic !== '-' ? item.pic : 'Admin');

  // ── Catatan
  const [catatan, setCatatan] = useState('');

  // ── Duplicate / save error indication shown at confirm time
  const [blockMessage, setBlockMessage] = useState('');

  // ── Validation
  const qtyValid = isIntegerString(qtyText) && Number(qtyText) >= QTY_MIN && Number(qtyText) <= QTY_MAX;
  const picValid = pic.trim().length >= 1 && pic.trim().length <= PIC_MAX;
  const branchesValid = branchRows.every((r) => isIntegerString(r.text) && Number(r.text) >= ALLOC_MIN && Number(r.text) <= ALLOC_MAX);
  const hasBranches = branchRows.length > 0;
  const canConfirm = qtyValid && picValid && hasBranches && branchesValid;

  // ── Quantity handlers
  const onQtyChange = (e) => {
    setBlockMessage('');
    const raw = e.target.value;
    // Clearing the field restores the suggested national deficit default.
    if (raw === '') {
      setQtyText(String(defaultQty));
      return;
    }
    setQtyText(raw);
  };

  // ── Branch allocation handlers
  const onBranchChange = (cabang, raw) => {
    setBlockMessage('');
    setBranchRows((rows) =>
      rows.map((r) => {
        if (r.cabang !== cabang) return r;
        const valid = isIntegerString(raw) && Number(raw) >= ALLOC_MIN && Number(raw) <= ALLOC_MAX;
        // Accept valid entries (commit value); reject invalid (retain last valid value)
        // while still surfacing the typed text so the operator can correct it.
        return { ...r, text: raw, value: valid ? Number(raw) : r.value };
      })
    );
  };

  const onBranchBlur = (cabang) => {
    // On blur, snap any invalid text back to the last valid value.
    setBranchRows((rows) =>
      rows.map((r) => {
        if (r.cabang !== cabang) return r;
        const valid = isIntegerString(r.text) && Number(r.text) >= ALLOC_MIN && Number(r.text) <= ALLOC_MAX;
        return valid ? r : { ...r, text: String(r.value) };
      })
    );
  };

  // ── Close helpers (restore focus to trigger, or first dashboard control)
  const restoreFocus = useCallback(() => {
    const el = triggerRef.current;
    if (el && document.body.contains(el) && typeof el.focus === 'function') {
      el.focus();
      return;
    }
    // Trigger no longer present: move focus to the first interactive control.
    const focusable = document.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && typeof focusable.focus === 'function') focusable.focus();
  }, []);

  const handleClose = useCallback(() => {
    onClose?.();
    // Defer so the dialog is unmounted before focus is restored.
    setTimeout(restoreFocus, 0);
  }, [onClose, restoreFocus]);

  // ── Confirm / create
  const handleConfirm = async () => {
    if (!canConfirm) {
      if (!qtyValid) setBlockMessage(`Qty Produksi harus bilangan bulat ${QTY_MIN}–${QTY_MAX.toLocaleString('id-ID')}.`);
      else if (!picValid) setBlockMessage('PIC wajib diisi (1–100 karakter).');
      else if (!hasBranches) setBlockMessage('Tidak ada defisit cabang untuk item ini.');
      else setBlockMessage('Ada alokasi cabang yang tidak valid.');
      return;
    }

    // Re-evaluate duplicate just before creating.
    const existing = await spkStore.getByKode(item.kode);
    if (existing) {
      setBlockMessage(`SPK untuk ${item.kode} sudah ada dan belum diterima. Tidak dapat membuat SPK baru.`);
      return;
    }

    const qtyProduksi = Number(qtyText);
    const defisitCabang = branchRows.map((r) => ({
      cabang: r.cabang,
      stok: r.stok,
      sp: r.sp,
      selisih: r.selisih,
      alloc: r.value,
    }));

    try {
      await spkStore.create({
        kode: item.kode,
        nama: item.nama,
        satuan: item.satuan,
        kategori: item.kategori,
        defisitNasional: defaultQty,
        defisitCabang,
        qtyProduksi,
        pic: pic.trim(),
        catatan: catatan.trim(),
      });
    } catch {
      setBlockMessage('SPK gagal disimpan. Silakan coba lagi.');
      return;
    }

    onCreated?.();
    handleClose();
  };

  // ── Focus management: move focus into the dialog on open.
  useEffect(() => {
    const t = setTimeout(() => {
      (firstFieldRef.current || panelRef.current)?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // ── Keyboard: Escape closes, Tab is trapped within the dialog.
  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
      return;
    }
    if (e.key === 'Tab') {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const onOverlayMouseDown = (e) => {
    if (e.target === overlayRef.current) handleClose();
  };

  const dialogLabel = `Buat SPK untuk ${item?.nama || item?.kode || 'item'}`;

  return (
    <div
      ref={overlayRef}
      onMouseDown={onOverlayMouseDown}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: 16,
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{
          background: C.white, borderRadius: 12, width: 'min(520px, 94vw)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(15,23,42,.30)', borderTop: `3px solid ${C.indigo}`,
          outline: 'none',
        }}
      >
        {/* ── Header (fixed) */}
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.line}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="pi pi-file-edit" style={{ color: C.indigo, fontSize: 15 }} />
              <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>Buat SPK</span>
            </div>
            <div style={{ fontSize: 12, color: C.sub, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Tinjau & sesuaikan sebelum membuat surat perintah kerja
            </div>
          </div>
          <button
            type="button"
            aria-label="Tutup"
            onClick={handleClose}
            style={{ border: 'none', background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4, borderRadius: 6 }}
          >
            <i className="pi pi-times" />
          </button>
        </div>

        {/* ── Body (scrolls vertically; no horizontal scroll) */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Item context (read-only) */}
          <div style={{ background: C.panel, border: `1px solid ${C.line}`, borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{item?.nama}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.indigo, background: '#eef2ff', padding: '1px 7px', borderRadius: 5, fontFamily: 'monospace' }}>{item?.kode}</span>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
              <div>
                <div style={labelStyle}>Kategori</div>
                <div style={{ fontSize: 12, color: C.slate, fontWeight: 600 }}>{item?.kategori || '-'}</div>
              </div>
              <div>
                <div style={labelStyle}>Satuan</div>
                <div style={{ fontSize: 12, color: C.slate, fontWeight: 600 }}>{item?.satuan || '-'}</div>
              </div>
              <div>
                <div style={labelStyle}>Defisit Nasional</div>
                <div style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>{defaultQty.toLocaleString('id-ID')} {item?.satuan || ''}</div>
              </div>
            </div>
          </div>

          {/* Production quantity */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="spk-qty" style={labelStyle}>Qty Produksi</label>
            <input
              id="spk-qty"
              ref={firstFieldRef}
              type="text"
              inputMode="numeric"
              value={qtyText}
              onChange={onQtyChange}
              aria-invalid={!qtyValid}
              style={{ ...fieldStyle, borderColor: qtyValid ? C.line : C.red }}
            />
            {!qtyValid && (
              <span style={errTextStyle}>
                Qty Produksi harus bilangan bulat antara {QTY_MIN} dan {QTY_MAX.toLocaleString('id-ID')} unit.
              </span>
            )}
          </div>

          {/* Branch deficit allocation */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>Alokasi Defisit per Cabang</span>
            {!hasBranches ? (
              <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', padding: '8px 12px', background: C.panel, border: `1px dashed ${C.line}`, borderRadius: 8 }}>
                Tidak ada data defisit cabang untuk item ini.
              </div>
            ) : (
              <div style={{ border: `1px solid ${C.line}`, borderRadius: 8, overflow: 'hidden' }}>
                {branchRows.map((r, idx) => {
                  const rowValid = isIntegerString(r.text) && Number(r.text) >= ALLOC_MIN && Number(r.text) <= ALLOC_MAX;
                  return (
                    <div key={r.cabang} style={{ borderTop: idx === 0 ? 'none' : `1px solid ${C.line}`, padding: '6px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.slate }}>{CABANG_LABELS[r.cabang] || r.cabang}</div>
                        <div style={{ fontSize: 11, color: C.sub, display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 1 }}>
                          <span>Stok <b style={{ color: C.slate }}>{Math.abs(r.stok).toLocaleString('id-ID')}</b></span>
                          <span>SP <b style={{ color: C.slate }}>{Math.abs(r.sp).toLocaleString('id-ID')}</b></span>
                          <span>Selisih <b style={{ color: r.selisih < 0 ? C.red : C.green }}>{Math.abs(r.selisih).toLocaleString('id-ID')}</b></span>
                        </div>
                      </div>
                      <div style={{ width: 110, flexShrink: 0 }}>
                        <input
                          type="text"
                          inputMode="numeric"
                          aria-label={`Alokasi ${CABANG_LABELS[r.cabang] || r.cabang}`}
                          aria-invalid={!rowValid}
                          value={r.text}
                          onChange={(e) => onBranchChange(r.cabang, e.target.value)}
                          onBlur={() => onBranchBlur(r.cabang)}
                          style={{ ...fieldStyle, padding: '4px 8px', fontSize: 12, textAlign: 'right', borderColor: rowValid ? C.line : C.red }}
                        />
                        {!rowValid && (
                          <div style={{ ...errTextStyle, marginTop: 2, textAlign: 'right' }}>
                            {ALLOC_MIN}–{ALLOC_MAX.toLocaleString('id-ID')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* PIC */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="spk-pic" style={labelStyle}>PIC (Penanggung Jawab)</label>
            <input
              id="spk-pic"
              type="text"
              list="spk-pic-options"
              value={pic}
              maxLength={PIC_MAX}
              onChange={(e) => { setBlockMessage(''); setPic(e.target.value); }}
              aria-invalid={!picValid}
              style={{ ...fieldStyle, borderColor: picValid ? C.line : C.red }}
            />
            <datalist id="spk-pic-options">
              {PIC_SUGGESTIONS.map((p) => <option key={p} value={p} />)}
            </datalist>
            {!picValid && <span style={errTextStyle}>PIC wajib diisi (1–{PIC_MAX} karakter).</span>}
          </div>

          {/* Catatan */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label htmlFor="spk-catatan" style={labelStyle}>Catatan (opsional)</label>
            <textarea
              id="spk-catatan"
              value={catatan}
              maxLength={CATATAN_MAX}
              onChange={(e) => setCatatan(e.target.value)}
              rows={2}
              style={{ ...fieldStyle, resize: 'vertical', minHeight: 44 }}
            />
            <span style={{ fontSize: 10, color: C.muted, textAlign: 'right' }}>{catatan.length}/{CATATAN_MAX}</span>
          </div>

          {blockMessage && (
            <div role="alert" style={{ fontSize: 12, color: C.red, background: C.redBg, border: '1px solid #fecaca', borderRadius: 8, padding: '8px 12px', fontWeight: 600 }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: 12, marginRight: 6 }} />{blockMessage}
            </div>
          )}
        </div>

        {/* ── Action area (fixed) */}
        <div style={{ padding: '10px 16px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'flex-end', gap: 8, background: C.white }}>
          <button
            type="button"
            onClick={handleClose}
            style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${C.line}`, background: C.white, color: C.slate, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            Batal
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: 'none',
              background: canConfirm ? C.indigo : '#c7d2fe', color: C.white, fontWeight: 700, fontSize: 13,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
          >
            <i className="pi pi-check" style={{ fontSize: 12 }} /> Buat SPK
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpkCreateDialog;
